import React from 'react';
import PropTypes from 'prop-types';
import Tabs from 'relements/build/esm/ui/tabs';
import Table from 'relements/build/esm/ui/table';
import Chips from 'relements/build/esm/input/chips';
import Icon from 'relements/build/esm/ui/icon';
import Pagination from 'relements/build/esm/ui/pagination';

import { LANGUAGE_CODE_TO_TITLE } from '@src/constants/Language';
import styles from './DictionaryTable.scss';

export default class DictionaryTable extends React.Component {
  static propTypes = {
    dictionary: PropTypes.object,
    onAdd: PropTypes.func,
    onChange: PropTypes.func,
    onDelete: PropTypes.func,
    onFilter: PropTypes.func,
    onAddLanguage: PropTypes.func,
    onLanguageChange: PropTypes.func,
    filters: PropTypes.shape({
      onlyEmptyVariants: PropTypes.bool,
      searchTerm: PropTypes.string,
      page: PropTypes.number,
      pageSize: PropTypes.number,
      numPages: PropTypes.number,
      numWords: PropTypes.number,
    }),
    supportedLanguages: PropTypes.arrayOf(PropTypes.string),
    enabledLanguages: PropTypes.arrayOf(PropTypes.string),
    language: PropTypes.string,
  };

  state = {};

  render() {
    return (
      <div className={styles.table}>
        {this.renderTableHeader()}
        {this.renderTableBody()}
        {this.renderTableFooter()}
      </div>
    );
  }

  renderTableHeader() {
    const { numWords } = this.props.filters;
    const RENDER_TABS = this._getTabs();
    return (
      <div className={styles.tableHeader}>
        <div className={styles.tableHeaderLeft}>Words({numWords})</div>
        <div className={styles.tableHeaderRight}>
          <div className={styles.tableHeaderRightLabel}>Variants:</div>
          <Tabs
            className={styles.tableHeaderRightTabs}
            value={this.props.language || ''}
            onChange={e => {
              this.props.onLanguageChange(e);
            }}
          >
            {RENDER_TABS.map(tab => (
              <Tabs.Item
                value={tab.path}
                onClick={e => {
                  this.props.onLanguageChange(e);
                }}
              >
                {tab.title}
              </Tabs.Item>
            ))}
          </Tabs>
        </div>
      </div>
    );
  }

  renderTableBody() {
    if (!this.props.language && !this.props.editable) {
      return (
        <span style={{ padding: '24px 16px' }}>
          No words in this dictionary
        </span>
      );
    }
    if (!this.props.language) {
      return (
        <span style={{ padding: '24px 16px' }}>
          Please enable a language for the dictionary
        </span>
      );
    }
    return (
      <div className={styles.tableBody}>
        <Table
          simple
          sortable
          className={styles.tableBodyContent}
          prefixClassName={styles.wordsTable}
          columns={this._getColumns()}
          rows={this._getRows()}
        />
      </div>
    );
  }

  renderAddButton() {
    return (
      <div className={styles.tableRowAdd} onClick={this._handleAddWord}>
        + Add Word
      </div>
    );
  }

  renderRowTitle(data, i) {
    return (
      <div className={styles.tableRowTitle}>
        <input
          value={data.word}
          onChange={e => {
            // remove . and $
            let value = e.target.value.replace(/[\.$]/g, '');
            // replace multi space with single space
            value = value.replace(/[ ]{2,}/g, ' ');
            this.props.onChange({
              key: data.key,
              type: 'word',
              value,
            });
          }}
          placeholder="Type here..."
          className={styles.tableRowTitleInput}
          disabled={!this.props.editable}
        />
      </div>
    );
  }

  renderRowVariants(variants, i, data) {
    return (
      <div className={styles.tableRowVariants}>
        <Chips
          className={styles.tableRowVariantsChips}
          placeholder="Add Variant"
          value={variants}
          onChange={variants =>
            this.props.onChange({
              key: data.key,
              type: 'variants',
              value: variants,
              language: this.props.language,
            })
          }
          disabled={!this.props.editable}
        />
      </div>
    );
  }

  renderRowCTAs(key) {
    return (
      <div
        onClick={() => this.props.onDelete(key)}
        className={styles.tableRowCTAs}
      >
        <Icon src="trash" className={styles.tableRowCTA} />
      </div>
    );
  }

  renderTableFooter() {
    const { numPages, page } = this.props.filters;
    return (
      <div className={styles.tableFooter}>
        <Pagination
          numPages={numPages}
          page={page}
          onChange={page => {
            this.props.onFilter('page', page);
          }}
          className={styles.tableFooterPagination}
        />
      </div>
    );
  }

  _getTabs = () => {
    const enabledLanguages = this.props.enabledLanguages || [];
    const { supportedLanguages } = this.props;
    return [...supportedLanguages]
      .sort((a, b) => {
        return enabledLanguages.indexOf(b) - enabledLanguages.indexOf(a);
      })
      .map(languageCode => {
        return {
          path: languageCode,
          title: LANGUAGE_CODE_TO_TITLE[languageCode],
        };
      });
  };

  _getColumns = () => {
    const columns = [
      {
        title: 'Words',
        sortKey: 'title',
        width: '300px',
      },
      {
        title: 'Variants',
        sortKey: 'priority',
      },
      {
        // For Actions / CTAs
        title: '',
        sortKey: 'actions',
        align: 'right',
        width: '60px',
      },
    ];

    return columns;
  };

  _getRows = () => {
    const { dictionary, language } = this.props;

    const addRowRow = {
      columns: [
        this._generateColumn(this.renderAddButton()),
        this._generateColumn(),
        this._generateColumn(),
      ],
      disabled: false,
      className: styles.tableRow,
    };

    if (this.props.editable)
      return [addRowRow].concat(dictionary.map(this._generateRow(language)));

    return dictionary.map(this._generateRow(language));
  };

  _generateRow = language => (data, i) => {
    const row = {
      columns: [],
      disabled: false,
      className: styles.tableRow,
      hidden: !data,
    };

    if (data) {
      row.columns.push(this._generateColumn(this.renderRowTitle(data, i)));
      const variants = (data.variants[language] || {}).value || [];
      row.columns.push(
        this._generateColumn(this.renderRowVariants(variants, i, data))
      );
      row.columns.push(this._generateColumn(this.renderRowCTAs(data.key)));
    }

    return row;
  };

  _generateColumn = content => ({
    content,
  });

  _handleAddWord = () => {
    this.props.onAdd(this.props.language);
  };

  _handleAddLanguage = newLanguage => {
    this.props.onAddLanguage(newLanguage);
  };
}
